import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import { Care } from 'src/replicant/buruburu/care';
import { CareItem } from 'src/replicant/buruburu/care/care.ruleset';
import { Pet } from 'src/replicant/buruburu/pet';
import StateObserver from 'src/StateObserver';
import sounds from 'src/lib/sounds';
import { CareActionType } from '../care/care.redux';
import { Conversation } from 'src/replicant/conversation';
import { Visit } from 'src/replicant/visit';
import { getRandomItemFromArray } from 'src/replicant/utils/random';
import { conversationActions } from '../conversation/conversation.redux';
import platform from '@play-co/gcinstant';
import LockedButtonOverlay from './LockedButtonOverlay';
import { getFriendsPet, getPet } from '../pet';

const ui = uiConfig.buruburu;

type CareActionButtonType = Exclude<CareActionType, 'evolve' | 'ascension'>;

export type CareActionSelection = {
  type: CareActionButtonType;
  careItemId?: CareItem['id'];
};
type CareActionButtonOpts = {
  type: CareActionButtonType;
  onSelect: (selection: CareActionSelection) => void;
  isLocked?: boolean;
};

export default class CareActionButton extends ButtonScaleViewWithText {
  private rulesetType: Care.ActionType | Care.ItemType;

  constructor(private opts: CareActionButtonOpts) {
    super({
      ...ui.menu.menuButton,
      icon: ui.care.actionIcons[opts.type],
      localeText: () => i18n(`careAction.${opts.type}`),
      tracking: { feature: 'careMenu', $subFeature: opts.type },
    });
    this.rulesetType =
      this.opts.type === 'firstAid' ? 'first_aid' : this.opts.type;

    this.icon.updateOpts({ scaleMethod: 'contain' });

    if (opts.isLocked) {
      this.canHandleEvents(false, true);
      const { width, height } = this.style;
      this.addSubview(new LockedButtonOverlay({ width, height }));
    }
  }

  onClick = async () => {
    sounds.sfx.play('ok');

    if (!this.checkIfCareActionAllowed()) {
      const careMenu = this.getSuperview().getSuperview(); // TODO decouple
      careMenu.emit('ForceClose');
      return;
    }

    const { type } = this.opts;
    let careItemId: CareItem['id'];
    switch (this.rulesetType) {
      case 'food':
      case 'snack':
      case 'toy':
      case 'first_aid':
        do {
          careItemId = await openPopupPromise('popupSelectCareItem', {
            type: this.rulesetType,
            stage: Pet.getters(
              StateObserver.getState().user,
            ).getCurrentEvolution().stage,
          });
          if (
            careItemId &&
            (await openPopupPromise('popupConfirmCareItem', { careItemId }))
          ) {
            this.opts.onSelect({ type, careItemId });
            break;
          }
        } while (careItemId);
        break;

      case 'toilet':
      case 'bath':
        this.opts.onSelect({ type });
        break;
    }
  };

  private checkIfCareActionAllowed() {
    const { user } = StateObserver.getState();
    const isVisiting = Visit.getters(user).isVisiting();
    const now = StateObserver.now();
    // On visiting allow only first_aid if friend pet is dying
    const isFriendDying = isVisiting && getFriendsPet()?.status === 'dying';
    // Make sure we're allowed to perform this care action
    const canPerformAction = Care.getters(user).canPerformCareAction(
      this.rulesetType,
      now,
    );
    const isCareActionsAllowed = !isFriendDying && canPerformAction;

    // Can perform
    if (isCareActionsAllowed) {
      return true;
    }

    // Allow first aid
    if (isFriendDying && this.rulesetType === 'first_aid') {
      return true;
    }

    // What pet should talk
    const pet = isFriendDying ? getFriendsPet() : getPet();

    // Show care_action_blocker conversation
    const dialogs = Conversation.getters(user).getValidDialogs(
      'care_action_blocker',
      pet.species,
      pet?.status,
      {},
    );

    const dialog = getRandomItemFromArray(dialogs, Math.random);
    if (dialog == null) return false;

    StateObserver.dispatch(
      conversationActions.setActiveDialog({
        id: pet.id,
        payload: dialog.id,
      }),
    );

    return false;
  }
}
